import React, { useState, useEffect } from "react";
import { auth, db } from "./firebase";
import { collection, addDoc, getDocs, doc, setDoc, getDoc, updateDoc, increment } from "firebase/firestore";

export default function Polls() {
  const [polls, setPolls] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchPolls(); }, []);

  const fetchPolls = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, "polls"));
    const data = await Promise.all(snap.docs.map(async (d) => {
      const poll = { id: d.id, ...d.data() };
      const voteSnap = await getDoc(doc(db, "polls", d.id, "voters", auth.currentUser.uid));
      poll.userVoted = voteSnap.exists() ? voteSnap.data().option : null;
      return poll;
    }));
    setPolls(data.sort((a, b) => b.createdAt - a.createdAt));
    setLoading(false);
  };

  const createPoll = async () => {
    const validOptions = options.filter(o => o.trim());
    if (!question.trim() || validOptions.length < 2) return;
    await addDoc(collection(db, "polls"), {
      question,
      options: validOptions,
      createdBy: auth.currentUser.uid,
      createdAt: Date.now(),
      expiresAt: Date.now() + 24 * 60 * 60 * 1000,
      status: "open",
      results: validOptions.reduce((acc, _, i) => ({ ...acc, [i]: 0 }), {}),
      totalVotes: 0,
    });
    setQuestion(""); setOptions(["", ""]); setShowForm(false);
    fetchPolls();
  };

  const vote = async (pollId, optionIdx) => {
    const voterRef = doc(db, "polls", pollId, "voters", auth.currentUser.uid);
    const exists = await getDoc(voterRef);
    if (exists.exists()) return;
    await setDoc(voterRef, { option: optionIdx, votedAt: Date.now() });
    await updateDoc(doc(db, "polls", pollId), {
      [`results.${optionIdx}`]: increment(1),
      totalVotes: increment(1),
    });
    fetchPolls();
  };

  const isExpired = (poll) => Date.now() > poll.expiresAt;

  const timeLeft = (expiresAt) => {
    const diff = expiresAt - Date.now();
    if (diff <= 0) return "Expired";
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return `${h}h ${m}m left`;
  };

  if (loading) return <p style={{ textAlign: "center", color: "#9CA3AF", marginTop: 40, fontSize: 13 }}>Loading...</p>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h2 style={S.title}>Polls</h2>
          <p style={S.subtitle}>Vote & share your opinion</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} style={S.createBtn}>+ New Poll</button>
      </div>

      {showForm && (
        <div style={S.formBox}>
          <textarea placeholder="Your question..." value={question} onChange={e => setQuestion(e.target.value)} style={S.textarea} />
          {options.map((opt, i) => (
            <input key={i} placeholder={`Option ${i + 1}`} value={opt}
              onChange={e => { const o = [...options]; o[i] = e.target.value; setOptions(o); }}
              style={S.input} />
          ))}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setOptions([...options, ""])} style={S.addOptBtn}>+ Option</button>
            <button onClick={createPoll} style={S.publishBtn}>Publish</button>
          </div>
        </div>
      )}

      {polls.length === 0 && <p style={{ textAlign: "center", color: "#9CA3AF", marginTop: 40, fontSize: 13 }}>No polls yet. Create one!</p>}

      {polls.map(poll => {
        const expired = isExpired(poll);
        const total = poll.totalVotes || 0;
        const showResults = expired || poll.userVoted !== null;

        return (
          <div key={poll.id} style={S.pollCard}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: expired ? "#9CA3AF" : "#D97706", letterSpacing: 0.5 }}>
                {expired ? "CLOSED" : "LIVE"}
              </span>
              <span style={{ fontSize: 11, color: "#9CA3AF" }}>{expired ? `${total} votes` : timeLeft(poll.expiresAt)}</span>
            </div>
            <p style={S.question}>{poll.question}</p>

            {poll.options.map((opt, i) => {
              const pct = total > 0 ? Math.round((poll.results[i] || 0) / total * 100) : 0;
              const isVoted = poll.userVoted === i;
              const isWinner = expired && pct === Math.max(...poll.options.map((_, j) => total > 0 ? Math.round((poll.results[j] || 0) / total * 100) : 0));

              return (
                <button key={i} onClick={() => !expired && poll.userVoted === null && vote(poll.id, i)}
                  style={{ ...S.optionBtn, border: `1.5px solid ${isVoted ? "#0D1B3E" : "transparent"}`, background: isWinner && expired ? "#F0F0F4" : isVoted ? "#F0F0F4" : "#F7F7F9", cursor: expired || poll.userVoted !== null ? "default" : "pointer", position: "relative", overflow: "hidden" }}>
                  {showResults && (
                    <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, background: "rgba(13,27,62,0.06)" }} />
                  )}
                  <div style={{ position: "relative", display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "#0D1B3E", fontWeight: isWinner ? 700 : 500 }}>{opt}</span>
                    {showResults && <span style={{ fontSize: 12, fontWeight: 700, color: "#0D1B3E" }}>{pct}%</span>}
                  </div>
                </button>
              );
            })}

            {!expired && poll.userVoted === null && (
              <p style={{ fontSize: 11, color: "#9CA3AF", textAlign: "center", marginTop: 8 }}>Results visible after 24 hours</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

const S = {
  title: { fontSize: 19, fontWeight: 700, color: "#0D1B3E", margin: 0 },
  subtitle: { fontSize: 13, color: "#9CA3AF", margin: "2px 0 0" },
  createBtn: { background: "#0D1B3E", color: "#fff", border: "none", borderRadius: 10, padding: "10px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  formBox: { background: "#fff", borderRadius: 16, padding: 18, marginBottom: 16, border: "1px solid #ECECEE" },
  textarea: { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #ECECEE", fontSize: 13, marginBottom: 10, resize: "none", height: 70, boxSizing: "border-box", background: "#FAFAFA" },
  input: { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #ECECEE", fontSize: 13, marginBottom: 8, boxSizing: "border-box", background: "#FAFAFA" },
  addOptBtn: { flex: 1, padding: 10, background: "#F7F7F9", border: "none", borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#0D1B3E" },
  publishBtn: { flex: 1, padding: 10, background: "#0D1B3E", color: "#fff", border: "none", borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  pollCard: { background: "#fff", borderRadius: 16, padding: 20, marginBottom: 12, border: "1px solid #ECECEE" },
  question: { fontSize: 14, fontWeight: 600, color: "#0D1B3E", margin: "0 0 12px", lineHeight: 1.4 },
  optionBtn: { width: "100%", padding: "11px 14px", borderRadius: 10, marginBottom: 8, textAlign: "left" },
};